import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  LayoutDashboard, FolderKanban, AlertTriangle, CheckCircle2,
  Clock, TrendingUp, Target, Layers,
} from 'lucide-react';
import { T, ROLES, springs } from '../lib/tokens';
import { Glass } from '../components/glass/Glass';
import { RoleBadge } from '../components/primitives/Badge';
import { ProgressBar } from '../components/primitives/ProgressBar';
import { SectionHeader } from '../components/cards/SectionHeader';

const PRIORITY_MAP = {
  1: { label: 'Critical', color: T.red },
  2: { label: 'High',     color: T.amber },
  3: { label: 'Medium',   color: T.blue },
  4: { label: 'Low',      color: T.textMuted },
};

// ─── Stat Card ──────────────────────────────────────────────────────────────

const StatCard = ({ icon: Icon, iconColor, iconBg, label, value, sub }) => (
  <Glass style={{ padding: 18 }} hover={false}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{
        width: 36, height: 36, borderRadius: 10,
        background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon size={18} color={iconColor} />
      </div>
      <div>
        <div style={{ fontSize: 22, fontWeight: 700, color: T.text }}>{value}</div>
        <div style={{ fontSize: 11, color: T.textMuted, fontWeight: 500 }}>{label}</div>
      </div>
      {sub && <div style={{ marginLeft: 'auto', fontSize: 11, color: T.textMuted }}>{sub}</div>}
    </div>
  </Glass>
);

// ─── Project Progress Card ──────────────────────────────────────────────────

const ProjectProgressCard = ({ project, milestones, tasks, onClick }) => {
  const projectTasks = tasks.filter(t => t.goal_id === project.id);
  const doneTasks = projectTasks.filter(t => t.done || t.status === 'done');
  const blockedTasks = projectTasks.filter(t => t.status === 'blocked');
  const overdueTasks = projectTasks.filter(t => t.deadline && new Date(t.deadline) < new Date() && !t.done);
  const progress = projectTasks.length > 0 ? Math.round((doneTasks.length / projectTasks.length) * 100) : 0;
  const stages = milestones.filter(m => m.goal_id === project.id);
  const activeStage = stages.find(m => m.is_active || m.stage_status === 'active');
  const completedStages = stages.filter(s => s.stage_status === 'completed' || s.done);
  const r = project.roles?.slug ? ROLES[project.roles.slug] : null;
  const prio = PRIORITY_MAP[project.priority] || PRIORITY_MAP[3];

  return (
    <motion.div whileHover={{ y: -2, boxShadow: T.shadow }} transition={springs.snappy} onClick={onClick} style={{ cursor: 'pointer' }}>
      <Glass style={{ padding: 18 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: T.text, lineHeight: 1.3, marginBottom: 4 }}>{project.title}</div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              {r && <RoleBadge roleSlug={project.roles.slug} small />}
              <span style={{ fontSize: 10, fontWeight: 600, color: prio.color }}>{prio.label}</span>
            </div>
          </div>
          <div style={{ fontSize: 20, fontWeight: 700, color: r?.color || T.amber }}>{progress}%</div>
        </div>

        <ProgressBar value={progress} color={r?.color || T.amber} height={5} />

        {/* Stage progress */}
        {stages.length > 0 && (
          <div style={{ display: 'flex', gap: 3, marginTop: 10 }}>
            {stages.sort((a, b) => a.sort_order - b.sort_order).map(s => (
              <div key={s.id} style={{
                flex: 1, height: 4, borderRadius: 2,
                background: s.stage_status === 'completed' || s.done ? T.green
                  : s.is_active || s.stage_status === 'active' ? T.blue
                  : 'rgba(0,0,0,0.06)',
              }} />
            ))}
          </div>
        )}

        <div style={{ display: 'flex', gap: 12, marginTop: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, color: T.textMuted }}>
            {doneTasks.length}/{projectTasks.length} tasks
          </span>
          {activeStage && (
            <span style={{ fontSize: 11, color: T.blue }}>
              {activeStage.title}
            </span>
          )}
          {stages.length > 0 && (
            <span style={{ fontSize: 11, color: T.textMuted }}>
              {completedStages.length}/{stages.length} stages
            </span>
          )}
          {overdueTasks.length > 0 && (
            <span style={{ fontSize: 10, fontWeight: 600, color: T.red, padding: '0 4px', borderRadius: 3, background: T.redLight }}>
              {overdueTasks.length} overdue
            </span>
          )}
          {blockedTasks.length > 0 && (
            <span style={{ fontSize: 10, fontWeight: 600, color: T.violet, padding: '0 4px', borderRadius: 3, background: T.violetLight }}>
              {blockedTasks.length} blocked
            </span>
          )}
        </div>

        {project.deadline && (
          <div style={{ marginTop: 8, fontSize: 10, color: new Date(project.deadline) < new Date() ? T.red : T.textMuted }}>
            Deadline: {new Date(project.deadline).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
          </div>
        )}
      </Glass>
    </motion.div>
  );
};

// ─── Overdue Tasks List ─────────────────────────────────────────────────────

const OverdueTasksList = ({ tasks }) => {
  const overdue = tasks
    .filter(t => t.deadline && new Date(t.deadline) < new Date() && !t.done && t.status !== 'done')
    .sort((a, b) => new Date(a.deadline) - new Date(b.deadline));

  if (overdue.length === 0) return null;

  return (
    <Glass style={{ padding: 18 }} hover={false}>
      <SectionHeader icon={AlertTriangle} iconBg={T.redLight} iconColor={T.red}
        title="Overdue Tasks" subtitle={`${overdue.length} tasks past deadline`} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
        {overdue.slice(0, 8).map(t => {
          const role = t.roles || t.goals?.roles;
          const r = role?.slug ? ROLES[role.slug] : null;
          const daysOverdue = Math.ceil((Date.now() - new Date(t.deadline).getTime()) / (1000 * 60 * 60 * 24));
          return (
            <div key={t.id} style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
              borderRadius: 6, background: 'rgba(224,88,88,0.04)', border: '1px solid rgba(224,88,88,0.08)',
            }}>
              <span style={{ fontSize: 12, color: T.text, flex: 1 }}>{t.title}</span>
              {r && <RoleBadge roleSlug={role.slug} small />}
              <span style={{ fontSize: 10, fontWeight: 600, color: T.red }}>
                {daysOverdue}d late
              </span>
            </div>
          );
        })}
        {overdue.length > 8 && (
          <div style={{ fontSize: 11, color: T.textMuted, paddingLeft: 10 }}>+{overdue.length - 8} more</div>
        )}
      </div>
    </Glass>
  );
};

// ─── Workspace Breakdown ────────────────────────────────────────────────────

const WorkspaceBreakdown = ({ tasks, goals }) => {
  const open = tasks.filter(t => !t.done && t.status !== 'done');
  const slugs = Object.keys(ROLES);

  return (
    <Glass style={{ padding: 18 }} hover={false}>
      <SectionHeader icon={Layers} iconBg={T.violetLight} iconColor={T.violet}
        title="By Workspace" />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 4 }}>
        {slugs.map(slug => {
          const r = ROLES[slug];
          const roleTasks = open.filter(t => t.roles?.slug === slug || t.goals?.roles?.slug === slug);
          const roleProjects = goals.filter(g => g.is_project && g.roles?.slug === slug && g.status === 'active');
          if (roleTasks.length === 0 && roleProjects.length === 0) return null;
          const totalMin = roleTasks.reduce((acc, t) => acc + (t.duration_minutes || 30), 0);
          return (
            <div key={slug}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: r.color }} />
                  <span style={{ fontSize: 12, fontWeight: 600, color: T.text }}>{r.label}</span>
                </div>
                <span style={{ fontSize: 11, color: T.textMuted }}>
                  {roleTasks.length} tasks · {roleProjects.length} projects
                </span>
              </div>
              <ProgressBar value={roleTasks.length} max={Math.max(open.length, 1)} color={r.color} height={4} />
              <div style={{ fontSize: 10, color: T.textMuted, marginTop: 3 }}>
                {Math.round(totalMin / 60)}h estimated work
              </div>
            </div>
          );
        })}
      </div>
    </Glass>
  );
};

// ─── Main DashboardView ─────────────────────────────────────────────────────

export const DashboardView = ({ goals, milestones, tasks, onUpdate, onSelectProject }) => {
  const projects = goals.filter(g => g.is_project && g.status === 'active');
  const openTasks = tasks.filter(t => !t.done && t.status !== 'done');
  const blockedTasks = openTasks.filter(t => t.status === 'blocked');
  const overdueTasks = openTasks.filter(t => t.deadline && new Date(t.deadline) < new Date());
  const atRiskTasks = openTasks.filter(t => t.eta && t.deadline && new Date(t.eta) > new Date(t.deadline));
  const completedToday = tasks.filter(t => {
    if (!t.done_at) return false;
    const doneDate = new Date(t.done_at).toISOString().split('T')[0];
    return doneDate === new Date().toISOString().split('T')[0];
  });

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={springs.gentle}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 28, fontWeight: 700, color: T.text, letterSpacing: '-0.03em' }}>Dashboard</h1>
        <p style={{ fontSize: 14, color: T.textMuted, marginTop: 4 }}>
          Overview across all projects and workspaces
        </p>
      </div>

      {/* Stat Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 24 }}>
        <StatCard icon={Target} iconColor={T.green} iconBg={T.greenLight}
          label="Active Projects" value={projects.length} />
        <StatCard icon={Clock} iconColor={T.blue} iconBg={T.blueLight}
          label="Open Tasks" value={openTasks.length}
          sub={`${completedToday.length} done today`} />
        <StatCard icon={AlertTriangle} iconColor={T.red} iconBg={T.redLight}
          label="Overdue" value={overdueTasks.length}
          sub={atRiskTasks.length > 0 ? `${atRiskTasks.length} at risk` : null} />
        <StatCard icon={CheckCircle2} iconColor={T.amber} iconBg={T.amberLight}
          label="Blocked" value={blockedTasks.length} />
      </div>

      {/* Main grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 20, alignItems: 'start' }}>
        {/* Left: Project cards */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <FolderKanban size={14} color={T.amber} />
            <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>Active Projects</span>
            <span style={{ fontSize: 11, color: T.textMuted }}>{projects.length}</span>
          </div>

          {projects.length === 0 ? (
            <Glass style={{ padding: 32, textAlign: 'center' }} hover={false}>
              <div style={{ fontSize: 13, color: T.textMuted }}>No active projects. Create one from the Projects view.</div>
            </Glass>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              {projects.sort((a, b) => (a.priority || 3) - (b.priority || 3)).map(p => (
                <ProjectProgressCard key={p.id} project={p} milestones={milestones} tasks={tasks}
                  onClick={() => onSelectProject && onSelectProject(p)} />
              ))}
            </div>
          )}
        </div>

        {/* Right: Overdue + Workspace */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <OverdueTasksList tasks={tasks} />
          <WorkspaceBreakdown tasks={tasks} goals={goals} />
        </div>
      </div>
    </motion.div>
  );
};
